import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, CheckCircle2, X, Archive, Trash2, ArchiveRestore } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TicketDetailsPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/support-tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/support-tickets/${ticketId}/comments`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTicket(response.data);
      setNewComment('');
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleMarkResolved = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/support-tickets/${ticketId}/status`,
        { status: 'resolved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTicket(response.data);
      toast.success('Ticket marked as resolved');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCloseTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/support-tickets/${ticketId}/status`,
        { status: 'closed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTicket(response.data);
      toast.success('Ticket closed');
    } catch (error) {
      console.error('Error closing ticket:', error);
      toast.error('Failed to close ticket');
    }
  };

  const handleArchive = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/support-tickets/${ticketId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket archived');
      setShowArchiveDialog(false);
      navigate('/support-tickets');
    } catch (error) {
      console.error('Error archiving ticket:', error);
      toast.error('Failed to archive ticket');
    }
  };

  const handleUnarchive = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/support-tickets/${ticketId}/unarchive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTicket(response.data);
      toast.success('Ticket unarchived');
    } catch (error) {
      console.error('Error unarchiving ticket:', error);
      toast.error('Failed to unarchive ticket');
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/support-tickets/${ticketId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket deleted');
      setShowDeleteDialog(false);
      navigate('/support-tickets');
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Failed to delete ticket');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Open', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In Progress', class: 'bg-yellow-100 text-yellow-800' },
      resolved: { label: 'Resolved', class: 'bg-green-100 text-green-800' },
      closed: { label: 'Closed', class: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      Low: { class: 'bg-gray-100 text-gray-800' },
      Normal: { class: 'bg-blue-100 text-blue-800' },
      High: { class: 'bg-red-100 text-red-800' }
    };
    const config = priorityConfig[priority] || priorityConfig.Normal;
    return <Badge className={config.class}>{priority}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen p-8 flex items-center justify-center">
          <p style={{ color: '#64748B' }}>Loading ticket details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout>
        <div className="min-h-screen p-8 flex items-center justify-center">
          <p style={{ color: '#64748B' }}>Ticket not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen p-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/support-tickets')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Tickets
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
                {ticket.subject}
              </h1>
              <div className="flex flex-col gap-1 text-sm" style={{ color: '#FFFFFF', opacity: 0.9 }}>
                <div className="flex items-center gap-2">
                  <span className="font-mono">#{ticket.ticket_id}</span>
                  <span>•</span>
                  <span>{formatDate(ticket.created_at)}</span>
                </div>
                <div style={{ color: '#FFFFFF', opacity: 0.7, fontSize: '0.875rem' }}>
                  Time: {formatTime(ticket.created_at)}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                <Button
                  onClick={handleMarkResolved}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Mark as Resolved
                </Button>
              )}
              {ticket.status !== 'closed' && (
                <Button
                  onClick={handleCloseTicket}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Close Ticket
                </Button>
              )}
              {ticket.is_archived ? (
                <Button
                  onClick={handleUnarchive}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <ArchiveRestore className="h-4 w-4" />
                  Unarchive
                </Button>
              ) : (
                <Button
                  onClick={() => setShowArchiveDialog(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Archive className="h-4 w-4" />
                  Archive
                </Button>
              )}
              <Button
                onClick={() => setShowDeleteDialog(true)}
                variant="outline"
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Details */}
            <Card>
              <CardHeader>
                <CardTitle>Ticket Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#245451' }}>
                    Description
                  </p>
                  <p className="text-base whitespace-pre-wrap" style={{ color: '#64748B' }}>
                    {ticket.description}
                  </p>
                </div>

                {ticket.attachment_url && (
                  <div>
                    <p className="text-sm font-semibold mb-2" style={{ color: '#245451' }}>
                      Attachment
                    </p>
                    <a
                      href={ticket.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Attachment
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Comments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {ticket.comments && ticket.comments.length > 0 ? (
                  <div className="space-y-4">
                    {ticket.comments.map((comment, index) => (
                      <div key={index} className="border-l-4 border-[#245451] pl-4 py-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold" style={{ color: '#245451' }}>
                            {comment.author}
                          </span>
                          <div className="flex flex-col items-end text-xs" style={{ color: '#94A3B8' }}>
                            <span>{formatDate(comment.created_at)}</span>
                            <span className="text-xs" style={{ color: '#B0BAC5' }}>{formatTime(comment.created_at)}</span>
                          </div>
                        </div>
                        <p style={{ color: '#64748B' }}>{comment.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4" style={{ color: '#94A3B8' }}>
                    No comments yet
                  </p>
                )}

                {/* Add Comment */}
                <div className="pt-4 border-t">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or note..."
                    className="mb-3"
                    rows={3}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={submittingComment || !newComment.trim()}
                    style={{ backgroundColor: '#245451', color: '#FFFFFF' }}
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#245451' }}>
                    Status
                  </p>
                  {getStatusBadge(ticket.status)}
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#245451' }}>
                    Priority
                  </p>
                  {getPriorityBadge(ticket.priority)}
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#245451' }}>
                    Category
                  </p>
                  <p style={{ color: '#64748B' }}>{ticket.category}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold mb-1" style={{ color: '#245451' }}>
                    Last Updated
                  </p>
                  <p style={{ color: '#64748B' }}>{formatDate(ticket.updated_at)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move the ticket out of your active list, but you can still view it later in the Archived filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the ticket and its history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default TicketDetailsPage;
